"use client";

import React from "react";
import { useTranslation } from "next-i18next";
import { useState, useMemo, useCallback } from "react";
import { Button } from "antd";
import { CloseCircleOutlined, CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons";
import { setProductFilterVisible } from "@/store/mobilePopup";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import DreamValueFilter from "@/components/Mall/DreamValueFilter";
import CategoryFilter from "@/components/Mall/CategoryFilter";
import BrandFilter from "@/components/Mall/BrandFilter";
import { IProductsQuery } from "@/props/IProduct";
import { Popup } from "@/components/antd-mobile";

/**
 * 可折叠区块（提到组件外部，避免父组件 re-render 时重新定义导致子组件 unmount）
 */
const CollapsibleSection = ({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="flex w-full flex-col border-b border-gray-100 py-3 last:border-b-0">
      <div className="flex cursor-pointer items-center justify-between" onClick={() => setOpen((prev) => !prev)}>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span>{title}</span>
          {count != null && count > 0 && (
            <span className="flex h-4 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white">
              {count}
            </span>
          )}
        </div>
        {open ? (
          <CaretUpOutlined className="text-xs text-stone-400" />
        ) : (
          <CaretDownOutlined className="text-xs text-stone-400" />
        )}
      </div>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

/**
 * mobile mall filters component
 */
const MobileMallFilter = ({ onMobileSearch }: { onMobileSearch: (params: IProductsQuery) => void }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { productFilterVisible } = useAppSelector((state: RootState) => state.mobilePopup);
  const [queryParams, setQueryParams] = useState<IProductsQuery>({});
  const [resetKey, setResetKey] = useState(0);

  // 计算已选数量
  const categoryCount = useMemo(() => {
    if (!queryParams.cid || queryParams.cid === "") return 0;
    return queryParams.cid.split(",").filter(Boolean).length;
  }, [queryParams.cid]);

  const brandCount = useMemo(() => {
    if (!queryParams.brandId || queryParams.brandId === "") return 0;
    return queryParams.brandId.split(",").filter(Boolean).length;
  }, [queryParams.brandId]);

  const hasAnyFilter = useMemo(() => {
    return categoryCount > 0 || brandCount > 0 || queryParams.minPrice != null || queryParams.maxPrice != null;
  }, [categoryCount, brandCount, queryParams.minPrice, queryParams.maxPrice]);

  /**
   * open or close mobile filters popup
   * @param visible boolean popup visible
   */
  const handleFilterVisible = (visible: boolean) => {
    dispatch(setProductFilterVisible(visible));
  };

  /**
   * 梦想值改变
   * @param priceArr
   */
  const onPriceChange = (priceArr: (number | null)[]) => {
    const data = JSON.parse(JSON.stringify(queryParams));
    data.minPrice = priceArr[0];
    data.maxPrice = priceArr[1];
    setQueryParams(data);
  };

  /**
   * 分类改变
   * @param cid
   */
  const onCategoryChange = (cid: string) => {
    const data = JSON.parse(JSON.stringify(queryParams));
    data.cid = cid;
    setQueryParams(data);
  };

  /**
   * 品牌改变
   * @param brandId
   */
  const onBrandChange = (brandId: string) => {
    const data = JSON.parse(JSON.stringify(queryParams));
    data.brandId = brandId;
    setQueryParams(data);
  };

  /**
   * handle confirm search
   */
  const handleSearch = () => {
    // close popup
    handleFilterVisible(false);
    // search
    onMobileSearch(queryParams);
  };

  // 清除所有筛选（只重置内部状态，等用户点确认时才请求）
  const handleClearAll = useCallback(() => {
    setQueryParams({});
    setResetKey((prev) => prev + 1);
  }, []);

  // filter header
  const FilterHeader = () => (
    <div className="flex items-center justify-between border-b border-gray-100 py-4">
      <div className="text-base font-semibold text-stone-800">{t("mobile.filter")}</div>
      {hasAnyFilter && (
        <div
          className="flex cursor-pointer items-center gap-1 text-xs text-primary"
          onClick={handleClearAll}
          data-qt="mall_filter_clear_all"
        >
          <CloseCircleOutlined className="text-xs" />
          {t("product_category.clear_all_filters")}
        </div>
      )}
    </div>
  ); 

  // filter body
  const FilterBody = (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <CollapsibleSection title={t("common.dream_value")}>
        <DreamValueFilter
          key={`dv-${resetKey}`}
          hideTitle
          onChange={(priceArr: (number | null)[]) => {
            onPriceChange(priceArr);
          }}
        />
      </CollapsibleSection>
      <CollapsibleSection title={t("product_category.product_category_title")} count={categoryCount}>
        <CategoryFilter
          key={`cat-${resetKey}`}
          hideTitle
          onChange={(cid: string) => {
            onCategoryChange(cid);
          }}
        />
      </CollapsibleSection>
      <CollapsibleSection title={t("product_category.brand_category")} count={brandCount}>
        <BrandFilter
          key={`brand-${resetKey}`}
          hideTitle
          onChange={(brandId: string) => {
            onBrandChange(brandId);
          }}
        />
      </CollapsibleSection>
    </div>
  );

  // filter actions
  const FilterActions = () => {
    return (
      <div className="flex justify-between py-4">
        <Button
          type="primary"
          className="h-9 flex-1"
          onClick={() => {
            handleFilterVisible(false);
          }}
          ghost
          data-qt="mall_filter_cancel"
        >
          {t("common.cancel")}
        </Button>
        <Button type="primary" className="ml-4 h-9 flex-1" onClick={handleSearch} data-qt="mall_filter_confirm">
          {t("common.confirm")}
        </Button>
      </div>
    );
  };

  return (
    <div className="block lg:hidden">
      <Popup
        visible={productFilterVisible}
        onMaskClick={() => {
          handleFilterVisible(false);
        }}
        onClose={() => {
          handleFilterVisible(false);
        }}
        position="right"
        bodyStyle={{ width: "70vw", maxWidth: "360px", minWidth: "260px" }}
      >
        <div className="flex h-svh w-full flex-col px-mobile">
          <FilterHeader />
          {FilterBody}
          <FilterActions />
        </div>
      </Popup>
    </div>
  );
};

export default MobileMallFilter;
